import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "errorsContainer",
    "errorItem",
    "errorDetails",
    "expandIcon",
    "searchInput",
    "filterButton",
    "groupSimilarCheckbox",
    "autoRefreshCheckbox",
    "errorCount",
    "warningCount",
    "lastUpdated"
  ]

  static values = {
    buildSessionId: String,
    currentFilter: { type: String, default: "all" },
    searchTerm: { type: String, default: "" },
    groupSimilar: { type: Boolean, default: true },
    autoRefresh: { type: Boolean, default: true },
    errors: { type: Array, default: [] },
    selectedErrorId: String
  }

  connect() {
    this.setupEventListeners()
    
    // Load initial errors from data attribute if present
    if (this.errorsValue && this.errorsValue.length > 0) {
      console.log('Loading initial errors from server:', this.errorsValue.length)
      // Don't re-render if errors are already displayed in HTML
      // Just ensure the array is populated for the action methods
      if (this.errorsContainerTarget && this.errorsContainerTarget.children.length === 0) {
        this.errorsValue.forEach(error => {
          this.addError(error)
        })
      }
    } else {
      // Fallback to API loading if no initial data
      this.loadInitialErrors()
    }
    
    this.startAutoRefresh()
  }
  
  // View error in modal
  viewError(event) {
    event.stopPropagation()
    const errorIndex = parseInt(event.currentTarget.dataset.errorId || '0')
    const error = this.errorsValue[errorIndex]
    
    if (error) {
      const modal = document.getElementById('error-details-modal')
      const content = document.getElementById('modal-error-content')
      
      // Build detailed error view
      content.innerHTML = `
        <div class="space-y-4">
          <div>
            <h4 class="font-semibold text-lg">${error.class_name || 'Error'}</h4>
            <p class="text-base-content/90 mt-1">${error.message || 'No message provided'}</p>
          </div>
          
          <div class="grid grid-cols-2 gap-4">
            <div>
              <span class="text-sm font-semibold">Path:</span>
              <p class="text-sm">${error.path || 'N/A'}</p>
            </div>
            <div>
              <span class="text-sm font-semibold">Method:</span>
              <p class="text-sm">${error.method || 'N/A'}</p>
            </div>
            <div>
              <span class="text-sm font-semibold">Timestamp:</span>
              <p class="text-sm">${error.timestamp || 'N/A'}</p>
            </div>
            <div>
              <span class="text-sm font-semibold">Source:</span>
              <p class="text-sm">${error.source || 'N/A'}</p>
            </div>
          </div>
          
          ${error.stack ? `
            <div>
              <h5 class="font-semibold mb-2">Stack Trace:</h5>
              <pre class="text-xs bg-base-300 p-3 rounded overflow-auto max-h-96">${error.stack}</pre>
            </div>
          ` : '<p class="text-sm text-base-content/60">No stack trace available</p>'}
          
          <div>
            <h5 class="font-semibold mb-2">Full Error Data:</h5>
            <pre class="text-xs bg-base-300 p-3 rounded overflow-auto max-h-48">${JSON.stringify(error, null, 2)}</pre>
          </div>
        </div>
      `
      
      modal.showModal()
    }
  }
  
  // Report bug - creates user story and adds to queue
  reportBug(event) {
    event.stopPropagation()
    const errorIndex = parseInt(event.currentTarget.dataset.errorId || '0')
    const error = this.errorsValue[errorIndex]
    
    if (!error) {
      console.error('Error not found for index:', errorIndex)
      return
    }
    
    console.log('Creating bug report for error:', error)
    
    // Create user story from error
    const userStoryData = {
      title: `Fix: ${error.message || error.class_name || 'Unknown Error'}`,
      description: this.formatErrorForTask(error),
      priority: this.getErrorPriority(error),
      phase: 'project_setup',
      state_type: 'unstarted',
      story_type: 'bug_fix',
      source_error: error
    }
    
    // Submit to create user story endpoint
    this.createTaskFromError(userStoryData, error)
  }
  
  // Fix bug - sends directly to Claude Code
  fixBug(event) {
    event.stopPropagation()
    const errorIndex = parseInt(event.currentTarget.dataset.errorId || '0')
    const error = this.errorsValue[errorIndex]
    
    if (!error) {
      console.error('Error not found for index:', errorIndex)
      return
    }
    
    console.log('Sending to Claude Code for immediate fix:', error)
    
    // Format error message for Claude Code
    const errorMessage = this.formatErrorForClaude(error)
    
    // Find and send to Claude Code chat
    this.findAndSendToClaudeCode(errorMessage)
    
    // Show success notification
    this.showSuccessToast('Error sent to Claude Code for immediate fix')
  }
  
  copyError(event) {
    event.stopPropagation()
    const errorIndex = parseInt(event.currentTarget.closest('[data-error-id]')?.dataset.errorId || '0')
    const error = this.errorsValue[errorIndex]
    
    if (error) {
      const errorText = JSON.stringify(error, null, 2)
      navigator.clipboard.writeText(errorText)
      this.showSuccessToast('Error copied to clipboard!')
    }
  }
  
  loadInitialErrors() {
    // Load errors from build session execution metrics
    const buildSessionId = this.buildSessionIdValue
    if (!buildSessionId) return
    
    // Fetch initial errors from the server
    fetch(`/api/build_sessions/${buildSessionId}/errors`)
      .then(response => {
        if (response.ok) return response.json()
        throw new Error('Failed to load errors')
      })
      .then(data => {
        if (data.errors && Array.isArray(data.errors)) {
          // Add each error to the display
          data.errors.forEach(error => {
            this.addError(error)
          })
        }
      })
      .catch(error => {
        console.error('Failed to load initial errors:', error)
      })
  }

  disconnect() {
    this.teardownEventListeners()
    this.stopAutoRefresh()
  }

  setupEventListeners() {
    // Listen for errors from both preview-devtools and devtools cable
    this.element.addEventListener('preview-devtools:error-message', this.handleErrorMessage.bind(this))
    this.element.addEventListener('devtools:error-update', this.handleErrorMessage.bind(this))
    this.element.addEventListener('preview-devtools:clear-errors', this.clearErrors.bind(this))
    this.element.addEventListener('preview-devtools:export-errors', this.exportErrors.bind(this))
  }

  teardownEventListeners() {
    this.element.removeEventListener('preview-devtools:error-message', this.handleErrorMessage.bind(this))
    this.element.removeEventListener('devtools:error-update', this.handleErrorMessage.bind(this))
    this.element.removeEventListener('preview-devtools:clear-errors', this.clearErrors.bind(this))
    this.element.removeEventListener('preview-devtools:export-errors', this.exportErrors.bind(this))
  }

  handleErrorMessage(event) {
    const errorData = event.detail
    this.addError(errorData)
  }

  addError(errorData) {
    // Add severity if not present
    if (!errorData.severity) {
      errorData.severity = this.inferSeverity(errorData)
    }
    
    // Parse stack trace if string
    if (typeof errorData.stack === 'string') {
      errorData.parsedStack = this.parseStackTrace(errorData.stack)
    }
    
    // Add timestamp if not present
    if (!errorData.timestamp) {
      errorData.timestamp = new Date().toISOString()
    }
    
    // Generate ID if not present
    errorData.id = errorData.id || `err-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    // Add to errors array
    this.errorsValue = [...this.errorsValue, errorData]
    
    // Update display
    this.renderErrors()
    this.updateCounts()
    this.updateLastUpdated()
    
    // Show toast notification for new errors
    this.showErrorNotification(errorData)
  }
  
  showErrorNotification(errorData) {
    const severity = errorData.severity || 'error'
    const message = errorData.message || 'An error occurred'
    const className = errorData.class_name || ''
    
    // Create toast with appropriate styling based on severity
    const toast = document.createElement('div')
    toast.className = 'toast toast-top toast-end z-50'
    
    const alertClass = severity === 'critical' || severity === 'fatal' ? 'alert-error' : 
                       severity === 'warning' ? 'alert-warning' : 'alert-error'
    
    const icon = severity === 'critical' || severity === 'fatal' ? 'fa-exclamation-circle' :
                 severity === 'warning' ? 'fa-exclamation-triangle' : 'fa-times-circle'
    
    toast.innerHTML = `
      <div class="alert ${alertClass} shadow-lg">
        <div>
          <i class="fas ${icon} mr-2"></i>
          <div>
            <h3 class="font-bold">${className || 'Error Detected'}</h3>
            <div class="text-xs">${this.truncateMessage(message, 100)}</div>
          </div>
        </div>
      </div>
    `
    
    document.body.appendChild(toast)
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      toast.classList.add('fade-out')
      setTimeout(() => toast.remove(), 300)
    }, 5000)
  }
  
  truncateMessage(message, maxLength) {
    if (message.length <= maxLength) return message
    return message.substring(0, maxLength) + '...'
  }

  inferSeverity(errorData) {
    const message = (errorData.message || '').toLowerCase()
    
    if (message.includes('critical') || message.includes('fatal')) {
      return 'critical'
    } else if (message.includes('error') || errorData.type === 'error') {
      return 'error'
    } else if (message.includes('warning') || message.includes('warn')) {
      return 'warning'
    } else {
      return 'info'
    }
  }

  parseStackTrace(stack) {
    if (!stack) return []
    
    const lines = stack.split('\n')
    const frames = []
    
    for (const line of lines) {
      const frame = this.parseStackFrame(line)
      if (frame) {
        frames.push(frame)
      }
    }
    
    return frames
  }

  parseStackFrame(line) {
    // Parse common stack trace formats
    // Chrome/Edge: at functionName (file:line:column)
    const chromeMatch = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/)
    if (chromeMatch) {
      return {
        method: chromeMatch[1],
        file: chromeMatch[2],
        line: chromeMatch[3],
        column: chromeMatch[4]
      }
    }
    
    // Firefox: functionName@file:line:column
    const firefoxMatch = line.match(/(.+?)@(.+?):(\d+):(\d+)/)
    if (firefoxMatch) {
      return {
        method: firefoxMatch[1],
        file: firefoxMatch[2],
        line: firefoxMatch[3],
        column: firefoxMatch[4]
      }
    }
    
    // Safari: functionName@file:line:column
    const safariMatch = line.match(/(.+?)@(.+?):(\d+):(\d+)/)
    if (safariMatch) {
      return {
        method: safariMatch[1],
        file: safariMatch[2],
        line: safariMatch[3],
        column: safariMatch[4]
      }
    }
    
    // Anonymous or file-only: file:line:column
    const fileOnlyMatch = line.match(/(.+?):(\d+):(\d+)/)
    if (fileOnlyMatch) {
      return {
        method: null,
        file: fileOnlyMatch[1],
        line: fileOnlyMatch[2],
        column: fileOnlyMatch[3]
      }
    }
    
    return null
  }

  renderErrors() {
    const filteredErrors = this.getFilteredErrors()
    const groupedErrors = this.groupSimilarValue ? this.groupErrors(filteredErrors) : filteredErrors
    
    if (groupedErrors.length === 0) {
      this.errorsContainerTarget.innerHTML = `
        <div class="flex items-center justify-center h-full text-base-content/40">
          <div class="text-center">
            <i class="fas fa-check-circle text-2xl text-success mb-2"></i>
            <p>No errors detected</p>
            <p class="text-xs mt-1">Errors will appear here when they occur</p>
          </div>
        </div>
      `
      return
    }
    
    this.errorsContainerTarget.innerHTML = ''
    const container = document.createElement('div')
    container.className = 'divide-y divide-base-300'
    
    groupedErrors.forEach((errorGroup, index) => {
      const errorElement = this.createErrorElement(errorGroup, index)
      container.appendChild(errorElement)
    })
    
    this.errorsContainerTarget.appendChild(container)
  }

  createErrorElement(errorGroup, index) {
    const div = document.createElement('div')
    div.className = 'flex items-center justify-between p-3 bg-base-200 rounded-lg hover:bg-base-300'
    div.dataset.errorId = errorGroup.id || index
    
    // Left side: Icon and error info
    const leftSide = document.createElement('div')
    leftSide.className = 'flex items-center gap-3 flex-1'
    
    // Severity icon
    const icon = document.createElement('i')
    const severity = errorGroup.severity || 'error'
    if (severity === 'error' || severity === 'critical') {
      icon.className = 'fas fa-exclamation-circle text-error text-lg'
    } else if (severity === 'warning') {
      icon.className = 'fas fa-exclamation-triangle text-warning text-lg'
    } else {
      icon.className = 'fas fa-info-circle text-info text-lg'
    }
    leftSide.appendChild(icon)
    
    // Error message
    const messageContainer = document.createElement('div')
    messageContainer.className = 'flex-1'
    
    const message = document.createElement('div')
    message.className = 'font-medium'
    message.textContent = errorGroup.message || errorGroup.class_name || 'Unknown error'
    messageContainer.appendChild(message)
    
    if (errorGroup.count > 1) {
      const count = document.createElement('div')
      count.className = 'text-xs text-base-content/60'
      count.textContent = `${errorGroup.count} occurrences`
      messageContainer.appendChild(count)
    }
    
    leftSide.appendChild(messageContainer)
    div.appendChild(leftSide)
    
    // Right side: Action buttons
    const rightSide = document.createElement('div')
    rightSide.className = 'flex items-center gap-2'
    
    // View button
    const viewBtn = document.createElement('button')
    viewBtn.className = 'btn btn-xs btn-ghost'
    viewBtn.textContent = 'View'
    viewBtn.dataset.action = 'click->error-tracker#viewError'
    viewBtn.dataset.errorId = index
    rightSide.appendChild(viewBtn)
    
    // Report Bug button
    const reportBtn = document.createElement('button')
    reportBtn.className = 'btn btn-xs btn-primary'
    reportBtn.textContent = 'Report Bug'
    reportBtn.dataset.action = 'click->error-tracker#reportBug'
    reportBtn.dataset.errorId = index
    rightSide.appendChild(reportBtn)
    
    // Fix Bug button
    const fixBtn = document.createElement('button')
    fixBtn.className = 'btn btn-xs btn-success'
    fixBtn.textContent = 'Fix Bug'
    fixBtn.dataset.action = 'click->error-tracker#fixBug'
    fixBtn.dataset.errorId = index
    rightSide.appendChild(fixBtn)
    
    // Ignore button
    const ignoreBtn = document.createElement('button')
    ignoreBtn.className = 'btn btn-xs btn-ghost text-error'
    ignoreBtn.textContent = 'Ignore'
    ignoreBtn.dataset.action = 'click->error-tracker#ignoreError'
    ignoreBtn.dataset.errorId = index
    rightSide.appendChild(ignoreBtn)
    
    div.appendChild(rightSide)
    
    return div
  }

  createErrorDetails(errorGroup) {
    const details = document.createElement('div')
    details.className = 'border-t border-base-300 bg-base-200/20'
    
    const detailsContent = document.createElement('div')
    detailsContent.className = 'p-3 space-y-3'
    
    // Stack trace
    if (errorGroup.parsedStack || errorGroup.stack) {
      const stackSection = document.createElement('div')
      
      const stackTitle = document.createElement('h4')
      stackTitle.className = 'text-xs font-semibold mb-2'
      stackTitle.textContent = 'Stack Trace'
      stackSection.appendChild(stackTitle)
      
      const stackContainer = document.createElement('div')
      stackContainer.className = 'bg-base-300/30 rounded p-2 overflow-x-auto'
      
      const frames = errorGroup.parsedStack || this.parseStackTrace(errorGroup.stack)
      if (frames && frames.length > 0) {
        frames.forEach(frame => {
          const frameDiv = document.createElement('div')
          frameDiv.className = 'text-xs font-mono hover:bg-base-300/50 px-1 py-0.5'
          
          if (frame.method) {
            const methodSpan = document.createElement('span')
            methodSpan.className = 'text-primary'
            methodSpan.textContent = frame.method
            frameDiv.appendChild(methodSpan)
            
            const atSpan = document.createElement('span')
            atSpan.className = 'text-base-content/60'
            atSpan.textContent = ' at '
            frameDiv.appendChild(atSpan)
          }
          
          const fileSpan = document.createElement('span')
          fileSpan.className = 'text-info'
          fileSpan.textContent = frame.file
          frameDiv.appendChild(fileSpan)
          
          const locationSpan = document.createElement('span')
          locationSpan.textContent = `:${frame.line}:${frame.column}`
          frameDiv.appendChild(locationSpan)
          
          stackContainer.appendChild(frameDiv)
        })
      } else {
        const noStack = document.createElement('span')
        noStack.className = 'text-xs text-base-content/40'
        noStack.textContent = 'No stack trace available'
        stackContainer.appendChild(noStack)
      }
      
      stackSection.appendChild(stackContainer)
      detailsContent.appendChild(stackSection)
    }
    
    // Additional context
    if (errorGroup.context) {
      const contextSection = document.createElement('div')
      
      const contextTitle = document.createElement('h4')
      contextTitle.className = 'text-xs font-semibold mb-2'
      contextTitle.textContent = 'Context'
      contextSection.appendChild(contextTitle)
      
      const contextContainer = document.createElement('div')
      contextContainer.className = 'bg-base-300/30 rounded p-2'
      
      const contextPre = document.createElement('pre')
      contextPre.className = 'text-xs'
      contextPre.textContent = JSON.stringify(errorGroup.context, null, 2)
      contextContainer.appendChild(contextPre)
      
      contextSection.appendChild(contextContainer)
      detailsContent.appendChild(contextSection)
    }
    
    // Actions
    const actions = document.createElement('div')
    actions.className = 'flex items-center gap-2'
    
    const copyButton = document.createElement('button')
    copyButton.className = 'btn btn-xs btn-ghost'
    copyButton.innerHTML = '<i class="fas fa-copy mr-1"></i> Copy'
    copyButton.addEventListener('click', () => this.copyError(errorGroup))
    actions.appendChild(copyButton)
    
    const reportButton = document.createElement('button')
    reportButton.className = 'btn btn-xs btn-ghost'
    reportButton.innerHTML = '<i class="fas fa-bug mr-1"></i> Report Issue'
    reportButton.addEventListener('click', () => this.reportIssue(errorGroup))
    actions.appendChild(reportButton)
    
    const ignoreButton = document.createElement('button')
    ignoreButton.className = 'btn btn-xs btn-ghost text-error'
    ignoreButton.innerHTML = '<i class="fas fa-eye-slash mr-1"></i> Ignore'
    ignoreButton.addEventListener('click', () => this.ignoreError(errorGroup))
    actions.appendChild(ignoreButton)
    
    detailsContent.appendChild(actions)
    details.appendChild(detailsContent)
    
    return details
  }

  toggleErrorDetails(errorId) {
    const errorItem = this.errorsContainerTarget.querySelector(`[data-error-id="${errorId}"]`)
    if (!errorItem) return
    
    const details = errorItem.querySelector('[data-error-tracker-target="errorDetails"]')
    const expandIcon = errorItem.querySelector('[data-error-tracker-target="expandIcon"]')
    
    if (details) {
      details.classList.toggle('hidden')
      expandIcon?.classList.toggle('rotate-180')
    }
  }

  getSeverityBadgeClass(severity) {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'fatal':
        return 'badge-error'
      case 'error':
        return 'badge-error badge-outline'
      case 'warning':
        return 'badge-warning'
      case 'info':
        return 'badge-info'
      default:
        return 'badge-ghost'
    }
  }

  getSeverityTextClass(severity) {
    switch (severity?.toLowerCase()) {
      case 'critical':
      case 'fatal':
        return 'text-error'
      case 'error':
        return 'text-error'
      case 'warning':
        return 'text-warning'
      case 'info':
        return 'text-info'
      default:
        return 'text-base-content'
    }
  }

  formatTimestamp(timestamp) {
    if (!timestamp) return ''
    
    try {
      const date = new Date(timestamp)
      return date.toLocaleTimeString('en-US', {
        hour12: false,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    } catch {
      return ''
    }
  }

  getFilteredErrors() {
    let filtered = [...this.errorsValue]
    
    // Apply severity filter
    if (this.currentFilterValue !== 'all') {
      filtered = filtered.filter(error => 
        error.severity?.toLowerCase() === this.currentFilterValue.toLowerCase()
      )
    }
    
    // Apply search filter
    if (this.searchTermValue) {
      const searchLower = this.searchTermValue.toLowerCase()
      filtered = filtered.filter(error => 
        error.message?.toLowerCase().includes(searchLower) ||
        error.file?.toLowerCase().includes(searchLower) ||
        error.stack?.toLowerCase().includes(searchLower)
      )
    }
    
    return filtered
  }

  groupErrors(errors) {
    if (!this.groupSimilarValue) return errors
    
    const groups = {}
    
    errors.forEach(error => {
      const key = `${error.message}-${error.file}-${error.line}`
      
      if (!groups[key]) {
        groups[key] = {
          ...error,
          count: 1,
          firstOccurrence: error.timestamp,
          lastOccurrence: error.timestamp
        }
      } else {
        groups[key].count++
        groups[key].lastOccurrence = error.timestamp
      }
    })
    
    return Object.values(groups)
  }

  updateCounts() {
    const errors = this.errorsValue
    const errorCount = errors.filter(e => e.severity === 'error' || e.severity === 'critical').length
    const warningCount = errors.filter(e => e.severity === 'warning').length
    
    if (this.hasErrorCountTarget) {
      this.errorCountTarget.textContent = errorCount
    }
    
    if (this.hasWarningCountTarget) {
      this.warningCountTarget.textContent = warningCount
    }
  }

  updateLastUpdated() {
    if (this.hasLastUpdatedTarget) {
      this.lastUpdatedTarget.textContent = 'just now'
    }
    
    // Update time ago every minute
    if (this.lastUpdatedInterval) {
      clearInterval(this.lastUpdatedInterval)
    }
    
    const startTime = Date.now()
    this.lastUpdatedInterval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const minutes = Math.floor(elapsed / 60000)
      
      if (minutes === 0) {
        this.lastUpdatedTarget.textContent = 'just now'
      } else if (minutes === 1) {
        this.lastUpdatedTarget.textContent = '1 minute ago'
      } else {
        this.lastUpdatedTarget.textContent = `${minutes} minutes ago`
      }
    }, 60000)
  }

  applyFilter(event) {
    const filter = event.currentTarget.dataset.filter
    this.currentFilterValue = filter
    
    // Update button states
    this.filterButtonTargets.forEach(btn => {
      if (btn.dataset.filter === filter) {
        btn.classList.add('btn-active')
      } else {
        btn.classList.remove('btn-active')
      }
    })
    
    this.renderErrors()
  }

  searchErrors(event) {
    this.searchTermValue = event.target.value
    this.renderErrors()
  }

  toggleGroupSimilar(event) {
    this.groupSimilarValue = event.target.checked
    this.renderErrors()
  }

  toggleAutoRefresh(event) {
    this.autoRefreshValue = event.target.checked
    
    if (this.autoRefreshValue) {
      this.startAutoRefresh()
    } else {
      this.stopAutoRefresh()
    }
  }

  startAutoRefresh() {
    if (!this.autoRefreshValue) return
    
    this.autoRefreshInterval = setInterval(() => {
      this.renderErrors()
      this.updateCounts()
    }, 5000) // Refresh every 5 seconds
  }

  stopAutoRefresh() {
    if (this.autoRefreshInterval) {
      clearInterval(this.autoRefreshInterval)
      this.autoRefreshInterval = null
    }
  }


  submitBug(event) {
    event.stopPropagation()
    const errorIndex = parseInt(event.currentTarget.closest('[data-error-id]')?.dataset.errorId || event.currentTarget.dataset.errorId || '0')
    const error = this.errorsValue[errorIndex]
    
    if (!error) {
      console.error('Error not found for index:', errorIndex)
      return
    }
    
    console.log('Submitting bug report for error:', error)
    
    // Create user story from error
    const userStoryData = {
      title: `Fix: ${error.message}`,
      description: this.formatErrorForTask(error),
      priority: this.getErrorPriority(error),
      phase: 'project_setup',
      state_type: 'unstarted',
      story_type: 'bug_fix',
      source_error: error
    }
    
    // Submit to create user story endpoint
    this.createTaskFromError(userStoryData, error)
  }

  sendToClaude(event) {
    event.stopPropagation()
    const errorIndex = parseInt(event.currentTarget.closest('[data-error-id]')?.dataset.errorId || event.currentTarget.dataset.errorId || '0')
    const error = this.errorsValue[errorIndex]
    
    if (!error) {
      console.error('Error not found for index:', errorIndex)
      return
    }
    
    console.log('Sending error to Claude:', error)
    
    // Format error message for Claude Code
    const errorMessage = this.formatErrorForClaude(error)
    
    // Find and send to Claude Code chat
    this.findAndSendToClaudeCode(errorMessage)
    
    // Show success notification
    this.showSuccessToast('Error sent to Claude Code for analysis')
  }
  
  findAndSendToClaudeCode(message) {
    console.log('Looking for Claude Code chat component...')
    
    // Find all Claude Code chat components
    const allChatComponents = document.querySelectorAll('[data-controller*="claude-code-chat"]')
    console.log('Found chat components:', allChatComponents.length)
    
    let chatComponent = null
    let messageInput = null
    
    // Find visible component
    for (const component of allChatComponents) {
      if (component.style.display === 'none' || component.closest('.hidden')) continue
      
      const input = component.querySelector('[data-claude-code-chat-target*="input"]')
      if (input && input.offsetParent !== null) {
        chatComponent = component
        messageInput = input
        break
      }
    }
    
    if (chatComponent && messageInput) {
      // Insert the message
      messageInput.value = message
      messageInput.focus()
      
      // Submit the form programmatically like the story execution does
      const form = messageInput.closest('form')
      if (form) {
        console.log('Submitting error to Claude Code via form')
        const submitEvent = new SubmitEvent('submit', { 
          bubbles: true, 
          cancelable: true,
          submitter: form.querySelector('button[type="submit"]')
        })
        form.dispatchEvent(submitEvent)
      } else {
        // Fallback to clicking submit button
        const submitButton = chatComponent.querySelector('[data-claude-code-chat-target="submit"]')
        if (submitButton) {
          console.log('Submitting error to Claude Code via button')
          submitButton.click()
        } else {
          console.warn('Submit button not found in Claude Code chat')
        }
      }
    } else {
      console.warn('Claude Code chat component not found or not visible')
      this.showErrorToast('Please open the Claude Code tab first')
    }
  }

  ignoreError(event) {
    event.stopPropagation()
    const errorIndex = parseInt(event.currentTarget.closest('[data-error-id]')?.dataset.errorId || '0')
    const error = this.errorsValue[errorIndex]
    
    if (error) {
      // Remove this error and similar ones
      this.errorsValue = this.errorsValue.filter(e => 
        !(e.message === error.message && e.class_name === error.class_name)
      )
      
      // Remove from DOM
      const errorCard = event.currentTarget.closest('[data-error-id]')
      if (errorCard) {
        errorCard.remove()
      }
      
      this.updateCounts()
      this.showSuccessToast('Error ignored')
    }
  }

  // Filter methods for the new UI
  showAll(event) {
    event.preventDefault()
    const cards = this.errorsContainerTarget.querySelectorAll('[data-error-id]')
    cards.forEach(card => card.style.display = 'block')
    
    // Update button states
    event.currentTarget.classList.add('btn-active')
    event.currentTarget.parentElement.querySelectorAll('button').forEach(btn => {
      if (btn !== event.currentTarget) btn.classList.remove('btn-active')
    })
  }
  
  showErrors(event) {
    event.preventDefault()
    const cards = this.errorsContainerTarget.querySelectorAll('[data-error-id]')
    cards.forEach((card, index) => {
      const error = this.errorsValue[index]
      if (error && (error.severity === 'error' || error.severity === 'critical' || !error.severity)) {
        card.style.display = 'block'
      } else {
        card.style.display = 'none'
      }
    })
    
    // Update button states
    event.currentTarget.classList.add('btn-active')
    event.currentTarget.parentElement.querySelectorAll('button').forEach(btn => {
      if (btn !== event.currentTarget) btn.classList.remove('btn-active')
    })
  }
  
  showWarnings(event) {
    event.preventDefault()
    const cards = this.errorsContainerTarget.querySelectorAll('[data-error-id]')
    cards.forEach((card, index) => {
      const error = this.errorsValue[index]
      if (error && error.severity === 'warning') {
        card.style.display = 'block'
      } else {
        card.style.display = 'none'
      }
    })
    
    // Update button states
    event.currentTarget.classList.add('btn-active')
    event.currentTarget.parentElement.querySelectorAll('button').forEach(btn => {
      if (btn !== event.currentTarget) btn.classList.remove('btn-active')
    })
  }
  
  filterErrors(event) {
    const searchTerm = event.currentTarget.value.toLowerCase()
    const cards = this.errorsContainerTarget.querySelectorAll('[data-error-id]')
    
    cards.forEach((card, index) => {
      const error = this.errorsValue[index]
      if (error) {
        const searchableText = [
          error.message,
          error.class_name,
          error.path,
          error.method
        ].filter(Boolean).join(' ').toLowerCase()
        
        if (searchableText.includes(searchTerm)) {
          card.style.display = 'block'
        } else {
          card.style.display = 'none'
        }
      }
    })
  }
  
  clearAllErrors(event) {
    event.preventDefault()
    if (confirm('Are you sure you want to clear all errors?')) {
      this.errorsValue = []
      this.errorsContainerTarget.innerHTML = `
        <div class="flex flex-col items-center justify-center h-64 text-base-content/50">
          <i class="fas fa-check-circle text-6xl text-success mb-4"></i>
          <p class="text-lg font-semibold">No Errors Detected</p>
          <p class="text-sm mt-2">Your application is running without errors</p>
        </div>
      `
      this.updateCounts()
      this.showSuccessToast('All errors cleared')
    }
  }
  
  clearErrors() {
    this.clearAllErrors({ preventDefault: () => {} })
  }

  exportErrors() {
    const data = {
      errors: this.errorsValue,
      filter: this.currentFilterValue,
      searchTerm: this.searchTermValue,
      groupSimilar: this.groupSimilarValue,
      exportedAt: new Date().toISOString()
    }
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `errors-${Date.now()}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  formatErrorForTask(error) {
    let description = `## Bug Report\n\n`
    description += `**Error Type**: ${error.error_class || error.type || 'Unknown'}\n`
    description += `**Message**: ${error.message}\n\n`
    
    if (error.path) {
      description += `**URL**: ${error.path}\n`
    }
    
    if (error.method) {
      description += `**HTTP Method**: ${error.method}\n`
    }
    
    if (error.stack && error.stack.length > 0) {
      description += `\n**Stack Trace**:\n\`\`\`\n`
      error.stack.slice(0, 5).forEach(frame => {
        description += `${frame.file}:${frame.line} in ${frame.method}\n`
      })
      description += `\`\`\`\n`
    }
    
    if (error.error_location) {
      description += `\n**Primary Location**: ${error.error_location.file}:${error.error_location.line} in \`${error.error_location.method}\`\n`
    }
    
    description += `\n**Captured At**: ${new Date(error.timestamp).toLocaleString()}\n`
    
    return description
  }

  formatErrorForClaude(error) {
    let message = `I found this error in the target application that needs debugging:\n\n`
    message += `**${error.error_class || error.type}**: ${error.message}\n\n`
    
    if (error.path) {
      message += `**Location**: ${error.method} ${error.path}\n`
    }
    
    if (error.error_location) {
      message += `**File**: ${error.error_location.file}:${error.error_location.line}\n`
      message += `**Method**: ${error.error_location.method}\n\n`
    }
    
    if (error.stack && error.stack.length > 0) {
      message += `**Stack trace**:\n`
      error.stack.slice(0, 3).forEach(frame => {
        message += `- ${frame.file}:${frame.line} in ${frame.method}\n`
      })
      message += `\n`
    }
    
    message += `Can you help me debug and fix this error?`
    
    return message
  }

  getErrorPriority(error) {
    const severity = error.severity?.toLowerCase()
    switch (severity) {
      case 'critical':
        return 'urgent'
      case 'error':
        return 'high'
      case 'warning':
        return 'medium'
      default:
        return 'low'
    }
  }

  async createTaskFromError(userStoryData, error) {
    try {
      const response = await fetch(`/app_projects/${this.getAppProjectSlug()}/user_stories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          user_story: userStoryData
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        this.showSuccessToast(`Bug story created: ${data.title}`)
        
        // Optionally add to story queue immediately
        this.addToStoryQueue(data.id)
      } else {
        const errorData = await response.json()
        this.showErrorToast(`Failed to create bug story: ${errorData.error || 'Unknown error'}`)
      }
    } catch (err) {
      console.error('Error creating user story:', err)
      this.showErrorToast('Failed to create bug story due to network error')
    }
  }


  getAppProjectSlug() {
    // Extract app project slug from current URL
    const pathParts = window.location.pathname.split('/')
    const appProjectIndex = pathParts.indexOf('app_projects')
    if (appProjectIndex !== -1 && pathParts[appProjectIndex + 1]) {
      return pathParts[appProjectIndex + 1]
    }
    return null
  }

  addToStoryQueue(storyId) {
    // Add the new bug story to the current build session queue
    // Request turbo_stream format to update the UI automatically
    fetch(`/app_projects/${this.getAppProjectSlug()}/build_sessions/${this.buildSessionIdValue}/add_stories_to_queue.turbo_stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({
        story_ids: [storyId]
      })
    }).then(response => {
      if (response.ok) {
        // Parse and apply the turbo stream response
        return response.text()
      }
      throw new Error('Failed to add to queue')
    }).then(turboStreamHTML => {
      // Apply the turbo stream updates to the page
      if (turboStreamHTML && window.Turbo) {
        Turbo.renderStreamMessage(turboStreamHTML)
      }
      this.showSuccessToast('Bug story added to execution queue')
    }).catch(err => {
      console.log('Note: Could not add to story queue automatically:', err.message)
    })
  }

  showSuccessToast(message) {
    const toast = document.createElement('div')
    toast.className = 'toast toast-top toast-end'
    toast.innerHTML = `
      <div class="alert alert-success">
        <span>${message}</span>
      </div>
    `
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 3000)
  }

  showErrorToast(message) {
    const toast = document.createElement('div')
    toast.className = 'toast toast-top toast-end'
    toast.innerHTML = `
      <div class="alert alert-error">
        <span>${message}</span>
      </div>
    `
    document.body.appendChild(toast)
    setTimeout(() => toast.remove(), 4000)
  }
}